/* eslint-disable @next/next/no-img-element */
import React from "react";
import { useRouter } from "next/router";
import { XIcon } from "@heroicons/react/outline";
import { Image, Pressable, Modal } from "@gonoodle/gn-universe-ui";
import { SECTIONS_TYPES } from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { twMerge } from "tailwind-merge";

import { useProfile, useOnSiteMessagesMutation } from "../../hooks";
import { useLogEvent } from "../../contexts/Analytics";
import {
  getSourcePageAndSourcePageTypeFromReferrer,
  getUtmParamsFromQueryParams,
} from "../../utils/analytics";
import Link from "../Link";
import { useUser } from "../../contexts/user";
import { useSDKStatus } from "../../contexts/OneTrust";
import { ROUTE_PREFIX, MESSAGE_TYPES, MESSAGE_STATUSES } from "../../constants";

function Growl({
  prompt,
  messageId,
  status,
  action = {},
  image,
  color,
  textTheme,
  anchorBounds = {},
}) {
  // Used to track if the message has been marked as displayed, so we don't hide it automatically.
  const markedAsDisplayedRef = React.useRef(false);
  const { user } = useUser();
  const { hasSelectedProfile } = useProfile();
  const {
    isBlocked: isOneTrustBlocked,
    isBannerOpen: isOneTrustBannerOpen,
  } = useSDKStatus();
  const { updateMessagesStatus } = useOnSiteMessagesMutation({
    // If the mutation fails, we'll use the error to roll back the optimistic update
    onError: () => {
      markedAsDisplayedRef.current = false;
    },
  });
  const isVisible =
    (status === MESSAGE_STATUSES.UNSEEN || markedAsDisplayedRef.current) &&
    status !== MESSAGE_STATUSES.DISMISSED &&
    (isOneTrustBlocked || isOneTrustBannerOpen === false) &&
    (user.isLoggedIn ? hasSelectedProfile : true); // Wait for the user to select a profile before showing the growl)

  React.useEffect(() => {
    if (isVisible && markedAsDisplayedRef.current === false) {
      markedAsDisplayedRef.current = true;
      updateMessagesStatus([
        { onSiteMessageId: messageId, action: MESSAGE_STATUSES.DISPLAYED },
      ]);
    }
  }, [messageId, isVisible, updateMessagesStatus]);

  const dismissMessage = () => {
    updateMessagesStatus([
      { onSiteMessageId: messageId, action: MESSAGE_STATUSES.DISMISSED },
    ]);
  };

  return (
    <Modal
      isOpen={isVisible}
      isDismissable={true}
      className="justify-end items-start"
      onClose={dismissMessage}
    >
      {/**
       * Removing pointer-events-none from the container to avoid breaking the modal click outside to close functionality,
       * as this container takes the whole screen and the modal is not able to detect the click outside.
       * TODO: Recreate the modal component to handle this case.
       */}
      <div className="absolute inset-0 container py-4 pointer-events-none">
        <div
          className="isolate flex flex-col w-[320px] mx-auto md:mx-0 md:ml-auto pointer-events-auto group"
          style={{
            transform: `translate(0px, ${anchorBounds.bottom}px)`,
          }}
        >
          {action.url && (
            <Link to={action.url} className="absolute inset-0 z-10" />
          )}

          <div className="relative h-[320px] rounded-t-lg overflow-hidden">
            <Image
              className="w-full h-full object-cover transform transition duration-200 group-hover:scale-110"
              sources={image}
              alt=""
            />

            <div className="absolute inset-0 bg-gradient-to-b from-[#18181B7A]" />

            <Pressable
              onPress={dismissMessage}
              className="absolute top-3 right-3 w-5 h-5 text-white z-20"
            >
              <XIcon />
            </Pressable>
          </div>

          <div
            className={twMerge(
              "flex flex-col p-6 space-y-4 rounded-b-lg",
              textTheme === "light" ? "text-white" : "text-gray-900",
            )}
            style={{
              backgroundColor: `#${color}`,
            }}
          >
            <div className="text-[26px] leading-[20px] font-extrabold font-display">
              {prompt}
            </div>

            <div className="text-sm">{action.name}</div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Banner({
  prompt,
  action = {},
  color,
  sponsor = {},
  internalTitle,
  textTheme,
}) {
  const router = useRouter();
  const [isVisible, setIsVisible] = React.useState(true);
  const { profile } = useProfile();

  const analyticsEventProperties = {
    sourceElement: SECTIONS_TYPES.ANNOUNCEMENT_BANNER,
    sourceName: prompt,
    title: internalTitle.toLowerCase(),
    ...getSourcePageAndSourcePageTypeFromReferrer(
      router.pathname,
      router.query,
    ),
    ...getUtmParamsFromQueryParams(router.query),
    gradeId: profile?.grade.id,
    size: profile?.size,
    schoolId: profile?.school.id,
    url: action.url,
  };

  useLogEvent({
    event: "Announcement banner shown",
    properties: {
      ...analyticsEventProperties,
    },
    options: {
      enabled: !!prompt,
    },
  });

  return (
    <div
      className={twMerge(
        "flex-row justify-around px-4 py-3 md:px-6 md:py-4",
        textTheme === "light" ? "text-white" : "text-gray-900",
        isVisible ? "flex" : "hidden",
      )}
      style={{
        backgroundColor: `#${color}`,
      }}
    >
      <div className="flex-1 flex flex-col md:flex-row justify-center mr-4">
        <div>
          <span className="text-sm lg:text-md">{prompt}</span>

          {action.name && action.url && (
            <Link
              to={action.url}
              className="text-sm lg:text-md whitespace-nowrap underline ml-1"
              events={[
                {
                  event: "Announcement banner clicked",
                  properties: {
                    ...analyticsEventProperties,
                  },
                },
              ]}
              queryParams={{
                referrer: {
                  sourceElement: SECTIONS_TYPES.ANNOUNCEMENT_BANNER,
                  sourceName: action.name,
                  ...getSourcePageAndSourcePageTypeFromReferrer(
                    router.pathname,
                    router.query,
                  ),
                },
              }}
            >
              {action.name}
            </Link>
          )}
        </div>

        {sponsor.logo && (
          <div className="flex flex-row items-center">
            <div className="hidden md:block bg-purple-100 w-[1px] h-full rounded mx-6" />

            <span className="text-[10px] md:text-[11px] font-bold leading-6 whitespace-nowrap">
              Powered by
            </span>

            <span className="h-3 w-20 md:w-36 md:h-7">
              <Image
                className="w-full h-full object-contain"
                sources={sponsor.logo}
                alt={sponsor.name || ""}
              />
            </span>
          </div>
        )}
      </div>

      <Pressable
        onPress={() => setIsVisible(false)}
        className="w-6 h-6 self-center"
      >
        <XIcon />
      </Pressable>
    </div>
  );
}

export default function OnSiteMessages({ anchorBounds }) {
  const router = useRouter();
  const { user } = useUser();
  const { onSiteMessages = [] } = user;

  if (router.pathname.startsWith(`/${ROUTE_PREFIX.CURRICULUM}/`)) {
    return null;
  }

  return onSiteMessages.map(
    ({
      displayType,
      onSiteMessageId,
      message,
      status,
      ctaPhrase,
      destinationUrl,
      logoAltText,
      logo,
      popoverDisplayImage,
      internalTitle,
      textTheme,
      backgroundColor,
    }) => {
      if (displayType === MESSAGE_TYPES.GROWL) {
        return (
          <Growl
            key={onSiteMessageId}
            prompt={message}
            messageId={onSiteMessageId}
            status={status}
            action={{
              name: ctaPhrase,
              url: destinationUrl,
            }}
            image={popoverDisplayImage}
            internalTitle={internalTitle}
            textTheme={textTheme}
            color={backgroundColor}
            anchorBounds={anchorBounds}
          />
        );
      }

      if (displayType === MESSAGE_TYPES.BANNER) {
        return (
          <Banner
            key={onSiteMessageId}
            prompt={message}
            action={{
              name: ctaPhrase,
              url: destinationUrl,
            }}
            sponsor={{
              name: logoAltText,
              logo,
            }}
            internalTitle={internalTitle}
            textTheme={textTheme}
            color={backgroundColor}
          />
        );
      }

      return null;
    },
  );
}
